import { SPREADSHEET_VIEW, TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { itemActionTypes } from "../../modules/items";
import { narrativeSelectors } from "../../modules/narrative";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getFlashPlaceholder,
  getIconPlaceholder,
  getSwitchViewInstruction,
  getTipIdGenerator,
  getViewNameLabel,
  Placeholders
} from "../utils";
import getSharedTip, { SharedTipIdentifiers } from "./sharedTips";

const isDesktop = preprocessorQuery.isDesktop();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "STORY_TO_TIMELINE";
const GROUP_LABEL = "From Story to Timeline";
const GROUP_DESCRIPTION = "Learn how to take elements of your existing story and build them into a timeline.";
const GROUP_GATE = state =>
  definitionSelectors.isNarrativeAllowed(state) &&
  narrativeSelectors.getNarrativeTreeIds(state).size >= 5 &&
  typeSelectors.getOrderedNarrativeTypes(state).size >= 2;
const GROUP_GATE_MESSAGE =
  "You must have Track Independent Narrative enabled and at least five items in your narrative to begin this tutorial.";
const GROUP_APPLICABLE_GATE = state => definitionSelectors.isNarrativeAllowed(state);
const GROUP_APPLICABLE_GATE_MESSAGE =
  "This tutorial only applies to timelines with Track Independent Narrative enabled.";
const GROUP_COMPLETED_MESSAGE = `Great work! Continue to develop your story timeline, and move on to adding ${Placeholders.AN_ENTITY_LABEL} when you are ready.`;
const GROUP_LINKS = [
  {
    label: "Learn other ways to view your narrative",
    path: "https://help.timeline.app/article/162-narrative-and-outline-views"
  },
  {
    label: "Chronological vs Narrative Order",
    path: "https://help.timeline.app/article/149-chronological-vs-narrative-order"
  }
  // {
  //   label: "How and Why to Create a Narrative From Your Timeline",
  //   path: "https://help.timeline.app/article/146-how-and-why-to-create-a-narrative-from-your-timeline"
  // }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("NARRATIVE_VS_CHRONOLOGY"),
      label: "Narrative vs Chronological Order",
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        {
          label: `${Placeholders.EVENT_PLURAL_LABEL} (not folders) from your narrative are visible in Spreadsheet`,
          isNumbered: true
        },
        {
          label: `Spreadsheet View allows you to order ${Placeholders.EVENT_PLURAL_LABEL} chronologically, even if they don't have dates`,
          isNumbered: true
        },
        {
          label: `Use the ${getFlashPlaceholder(
            interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
            "drag handles"
          )} ${getIconPlaceholder("dragHandle")} next to ${
            Placeholders.EVENT_PLURAL_LABEL
          } to drag them to a new position`,
          isNumbered: true
        },
        {
          label: `Drop between ${Placeholders.EVENT_PLURAL_LABEL} to re-order, or on top of ${Placeholders.EVENT_PLURAL_LABEL} to nest within`,
          isNumbered: true
        },
        {
          label: `Moving ${Placeholders.EVENT_PLURAL_LABEL} in Spreadsheet View does not move them in your narrative`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    getSharedTip(SharedTipIdentifiers.DRAG_TO_TIMELINE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.DRAG_TO_RESIZE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR, GROUP_ID),
    {
      id: idGenerator("ADD_EVENT_TIMELINE"),
      label: `Add a backstory ${Placeholders.EVENT_LABEL} to your timeline`,
      steps: [
        {
          label: `Double click in ${getViewNameLabel(TIMELINE_VIEW)} View to create a new ${
            Placeholders.EVENT_LABEL
          } at the date you click`,
          isNumbered: true
        },
        { label: `Type a label for the ${Placeholders.EVENT_LABEL} and press Enter`, isNumbered: true },
        {
          label: `The item will be inserted into the correct location in Spreadsheet View based on its date`,
          isNumbered: true
        },
        {
          label: `The item WILL NOT be added to your narrative automatically (but you could drag it there later)`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === itemActionTypes.CREATE_ITEM &&
        typeSelectors.getEventTypeIds(newState).includes(action.itemType)
    },
    {
      id: idGenerator("REMOVE_FROM_CHRONOLOGY"),
      label: `Remove narrative items from your chronological order`,
      steps: [
        {
          label:
            "If your narrative contains exposition text such as descriptions or internal thoughts and dreams, you can remove them from other views",
          isNumbered: true
        },
        {
          label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} that you would like to remove from the chronological order`,
          isNumbered: true
        },
        {
          label: `In the Inspector, change the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_TYPE,
            "item type"
          )} to ${Placeholders.NARRATIVE_EXPOSITION_LABEL}`,
          isNumbered: true
        },
        { label: `The item will be excluded from your chronological order`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresInspector: true,
      requiresDismissal: true
    }
  ];
} else {
  tips = [];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
